import { useState, useEffect } from "react";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Download, Package, ShoppingCart, ChevronDown, Plus, X, CreditCard, Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function ReflectionLibrary() {
  useSEO("reflection-library");
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      http.get("/reflection-library/public/settings"),
      http.get("/reflection-library/public/products"),
    ]).then(([s, p]) => {
      setSettings(s.data);
      setProducts(p.data);
    }).catch(() => toast.error("Could not load library."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (category === "All") {
      http.get("/reflection-library/public/products").then(r => setProducts(r.data));
    } else {
      http.get(`/reflection-library/public/products?category=${encodeURIComponent(category)}`).then(r => setProducts(r.data));
    }
  }, [category]);

  if (loading) return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#D4AF37] tracking-[0.4em] text-xs uppercase animate-pulse font-sans">Loading...</div>
      </div>
    </Layout>
  );

  if (!settings?.enabled) return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-mc-h text-3xl text-[#F5F5F0] mb-4">Coming Soon</h1>
          <p className="font-mc-b text-zinc-500 mb-8">The Reflection Library is being prepared.</p>
          <Link to="/mirror-conversations" className="text-[#D4AF37] font-mc-b text-sm underline underline-offset-4">Back to Mirror Conversations</Link>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-mc-h { font-family: 'Cormorant Garamond', serif; }
        .font-mc-b { font-family: 'DM Sans', sans-serif; }
        .gold-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent); }
      `}</style>

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-28 pb-16 px-6 text-center" data-testid="rl-hero">
        <div className="max-w-3xl mx-auto">
          <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Mirror Conversations&trade;</p>
          <h1 className="font-mc-h text-4xl md:text-6xl font-light text-[#F5F5F0] mb-4">{settings.hero_title}</h1>
          <p className="font-mc-b text-base text-[#D4AF37] mb-4 italic">{settings.hero_subtitle}</p>
          <p className="font-mc-b text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">{settings.hero_text}</p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="bg-[#0A0A0A] px-6 pb-12">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-2 justify-center" data-testid="rl-categories">
          {settings.categories?.map(cat => (
            <button key={cat} onClick={() => setCategory(cat)} data-testid={`cat-${cat}`}
              className={`px-4 py-2 rounded-full font-mc-b text-xs tracking-wide transition-all duration-200 ${
                category === cat
                  ? "bg-[#D4AF37] text-[#0A0A0A]"
                  : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/40 hover:text-zinc-300"
              }`}>{cat}</button>
          ))}
        </div>
      </section>

      {/* Product Grid */}
      <section className="bg-[#0A0A0A] px-6 pb-28">
        <div className="max-w-6xl mx-auto">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-mc-h text-2xl text-zinc-600">No worksheets available yet.</p>
              <p className="font-mc-b text-sm text-zinc-700 mt-2">Check back soon.</p>
            </div>
          ) : (
            <motion.div initial="hidden" animate="visible" variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <motion.div key={p.id} variants={fadeUp}>
                  <ProductCard product={p} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D0D0D] py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="gold-divider mb-8" />
          <p className="font-mc-h text-xl text-zinc-400 font-light italic mb-8">{settings.cta_text}</p>
          <Link to={settings.cta_link || "/mirror-conversations#packages"} className="inline-block border border-[#D4AF37] text-[#D4AF37] px-10 py-4 rounded-full font-mc-b text-sm tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-testid="rl-cta">
            Take a Seat
          </Link>
          <div className="mt-6">
            <Link to="/mirror-conversations" className="font-mc-b text-xs text-zinc-600 hover:text-[#D4AF37] transition-colors underline underline-offset-4">Back to Mirror Conversations</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function ProductCard({ product }) {
  const p = product;
  const lowestPrice = Math.min(...[p.digital_price, p.print_price, p.bundle_price].filter(v => v > 0)) || 0;
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300 group flex flex-col" data-testid={`product-card-${p.slug}`}>
      <div className="aspect-[4/3] bg-[#111] relative overflow-hidden">
        {p.thumbnail_url ? (
          <img src={resolveImage(p.thumbnail_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700 font-mc-h text-2xl">Preview</div>
        )}
        {p.is_bundle && <div className="absolute top-3 left-3 bg-[#D4AF37] text-[#0A0A0A] font-mc-b text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full">Bundle</div>}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-mc-h text-lg text-[#F5F5F0] mb-2">{p.title}</h3>
        <p className="font-mc-b text-xs text-zinc-500 leading-relaxed mb-4 flex-1">{p.short_desc}</p>
        {p.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {p.tags.slice(0, 4).map(tag => (
              <span key={tag} className="font-mc-b text-[10px] tracking-wide text-zinc-600 border border-white/[0.06] px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}
        {lowestPrice > 0 && (
          <p className="font-mc-b text-xs text-zinc-500 mb-4">From <span className="text-[#D4AF37] font-medium">${lowestPrice}</span></p>
        )}
        <Link to={`/reflection-library/${p.slug}`} className="w-full text-center bg-[#D4AF37] text-[#0A0A0A] py-3 rounded-full font-mc-b text-xs tracking-wide uppercase hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all" data-testid={`view-${p.slug}`}>
          View Details & Buy
        </Link>
      </div>
    </div>
  );
}

/* ════════════ PRODUCT DETAIL PAGE ════════════ */
export function ReflectionProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutFormat, setCheckoutFormat] = useState(null);

  useEffect(() => {
    http.get(`/reflection-library/public/products/${slug}`)
      .then(r => setProduct(r.data))
      .catch(() => { toast.error("Product not found"); navigate("/reflection-library"); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading || !product) return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#D4AF37] tracking-[0.4em] text-xs uppercase animate-pulse">Loading...</div>
      </div>
    </Layout>
  );

  const p = product;

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-mc-h { font-family: 'Cormorant Garamond', serif; }
        .font-mc-b { font-family: 'DM Sans', sans-serif; }
        .gold-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent); }
      `}</style>

      <section className="bg-[#0A0A0A] pt-24 pb-20 px-6" data-testid="product-detail">
        <div className="max-w-6xl mx-auto">
          <Link to="/reflection-library" className="inline-flex items-center gap-2 text-zinc-600 hover:text-[#D4AF37] font-mc-b text-xs tracking-wide uppercase mb-8 transition-colors">
            <ArrowLeft size={14} /> Back to Library
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Preview Image */}
            <div className="relative">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#111] border border-white/[0.06]">
                {p.thumbnail_url ? (
                  <img src={resolveImage(p.thumbnail_url)} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700 font-mc-h text-3xl">Preview</div>
                )}
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-sm rounded-lg py-2 px-4 text-center">
                <p className="font-mc-b text-[10px] text-zinc-500 tracking-wider uppercase">Watermarked Preview</p>
              </div>
            </div>

            {/* Details */}
            <div>
              {p.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {p.tags.map(tag => (
                    <span key={tag} className="font-mc-b text-[10px] tracking-wide text-[#D4AF37] border border-[#D4AF37]/20 px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              )}
              <h1 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0] mb-4">{p.title}</h1>
              <p className="font-mc-b text-base text-zinc-400 leading-relaxed mb-8">{p.full_desc || p.short_desc}</p>

              {/* Purchase Options with Buy Buttons */}
              <div className="space-y-3 mb-8">
                {(p.available_as === "digital" || p.available_as === "both") && p.digital_price > 0 && (
                  <button onClick={() => setCheckoutFormat("digital")} className="w-full flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-[#D4AF37]/40 transition-colors group text-left" data-testid="buy-digital">
                    <div className="flex items-center gap-3">
                      <Download size={16} className="text-[#D4AF37]" />
                      <div>
                        <span className="font-mc-b text-sm text-[#F5F5F0] block">Digital Download</span>
                        <span className="font-mc-b text-[10px] text-zinc-600">Instant PDF access</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-mc-h text-xl text-[#D4AF37] block">${p.digital_price}</span>
                      <span className="font-mc-b text-[10px] text-zinc-600 group-hover:text-[#D4AF37] transition-colors uppercase tracking-wider">Buy Now</span>
                    </div>
                  </button>
                )}
                {(p.available_as === "print" || p.available_as === "both") && p.print_price > 0 && (
                  <button onClick={() => setCheckoutFormat("print")} className="w-full flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-[#D4AF37]/40 transition-colors group text-left" data-testid="buy-print">
                    <div className="flex items-center gap-3">
                      <Package size={16} className="text-[#D4AF37]" />
                      <div>
                        <span className="font-mc-b text-sm text-[#F5F5F0] block">Printed Copy</span>
                        <span className="font-mc-b text-[10px] text-zinc-600">Mailed via First-Class</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-mc-h text-xl text-[#D4AF37] block">${p.print_price}</span>
                      <span className="font-mc-b text-[10px] text-zinc-600 group-hover:text-[#D4AF37] transition-colors uppercase tracking-wider">Buy Now</span>
                    </div>
                  </button>
                )}
                {p.available_as === "both" && p.bundle_price > 0 && (
                  <button onClick={() => setCheckoutFormat("both")} className="w-full flex items-center justify-between rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-4 hover:border-[#D4AF37]/60 transition-colors group text-left" data-testid="buy-both">
                    <div className="flex items-center gap-3">
                      <ShoppingCart size={16} className="text-[#D4AF37]" />
                      <div>
                        <span className="font-mc-b text-sm text-[#F5F5F0] block">Digital + Printed <span className="text-[10px] text-[#D4AF37] ml-1 uppercase tracking-wider">Best Value</span></span>
                        <span className="font-mc-b text-[10px] text-zinc-600">PDF + mailed copy</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-mc-h text-xl text-[#D4AF37] block">${p.bundle_price}</span>
                      <span className="font-mc-b text-[10px] text-zinc-600 group-hover:text-[#D4AF37] transition-colors uppercase tracking-wider">Buy Now</span>
                    </div>
                  </button>
                )}
              </div>

              {/* Info Blocks */}
              {p.who_for && <InfoBlock title="Who this is for" text={p.who_for} />}
              {p.whats_included && <InfoBlock title="What's included" text={p.whats_included} />}
              {p.how_to_use && <InfoBlock title="How to use it" text={p.how_to_use} />}

              {/* CTA */}
              <div className="gold-divider my-10" />
              <div className="text-center">
                <p className="font-mc-h text-lg text-zinc-500 font-light italic mb-4">Sometimes a worksheet is enough. Sometimes you need a conversation.</p>
                <Link to="/mirror-conversations#packages" className="inline-block border border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-full font-mc-b text-xs tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Take a Seat</Link>
              </div>
            </div>
          </div>

          {/* Related */}
          {p.related?.length > 0 && (
            <div className="mt-20">
              <h3 className="font-mc-h text-2xl text-[#F5F5F0] mb-8">Related Worksheets</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {p.related.map(r => <ProductCard key={r.id} product={r} />)}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Checkout Modal */}
      <AnimatePresence>
        {checkoutFormat && <CheckoutModal product={p} format={checkoutFormat} onClose={() => setCheckoutFormat(null)} />}
      </AnimatePresence>
    </Layout>
  );
}

/* ════════════ CHECKOUT MODAL ════════════ */
function CheckoutModal({ product, format, onClose }) {
  const needsShipping = format === "print" || format === "both";
  const price = format === "digital" ? product.digital_price : format === "print" ? product.print_price : product.bundle_price;
  const [form, setForm] = useState({ name: "", email: "", line1: "", line2: "", city: "", state: "", zip: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = "auto"; }; }, []);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (needsShipping && (!form.line1 || !form.city || !form.state || !form.zip)) {
      toast.error("Please fill in your shipping address.");
      return;
    }
    setLoading(true);
    try {
      const body = {
        items: [{ product_id: product.id, format }],
        name: form.name,
        email: form.email,
        shipping_address: needsShipping ? { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip } : null,
      };
      const { data } = await http.post("/reflection-library/public/checkout", body);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed.");
      setLoading(false);
    }
  };

  const formatLabel = format === "digital" ? "Digital Download" : format === "print" ? "Printed Copy" : "Digital + Printed";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#111] border border-[#D4AF37]/30 rounded-2xl w-full max-w-lg relative overflow-hidden flex flex-col max-h-[90vh]" data-testid="checkout-modal">
        <div className="p-6 border-b border-white/[0.06] flex justify-between items-start">
          <div>
            <h3 className="font-mc-h text-xl text-[#F5F5F0]">{product.title}</h3>
            <p className="font-mc-b text-xs text-[#D4AF37] mt-1">{formatLabel} — ${price}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <form onSubmit={handleCheckout} className="p-6 overflow-y-auto space-y-4">
          <div>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1 block">Full Name *</label>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1 block">Email *</label>
            <input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors" />
          </div>

          {needsShipping && (
            <div className="space-y-3 pt-2">
              <p className="font-mc-b text-xs tracking-[0.15em] uppercase text-[#D4AF37]">Shipping Address</p>
              <input required placeholder="Street Address *" value={form.line1} onChange={e => setForm({ ...form, line1: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
              <input placeholder="Apt, Suite (optional)" value={form.line2} onChange={e => setForm({ ...form, line2: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
              <div className="grid grid-cols-3 gap-3">
                <input required placeholder="City *" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
                <input required placeholder="State *" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
                <input required placeholder="ZIP *" value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-2 focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-zinc-700 text-sm" />
              </div>
              <p className="font-mc-b text-[10px] text-zinc-600">Shipped via USPS First-Class Mail</p>
            </div>
          )}

          <button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-mc-b tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50 mt-4" data-testid="checkout-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {loading ? "Processing..." : `Pay $${price} via Stripe`}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function InfoBlock({ title, text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06] py-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="font-mc-b text-sm text-[#F5F5F0]">{title}</span>
        <ChevronDown size={16} className={`text-zinc-600 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="font-mc-b text-sm text-zinc-400 leading-relaxed pt-3 whitespace-pre-line">{text}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
